// Utils
import { find, isNil, isNilOrEmpty, isNotNil, path, propEq, uniqBy } from './general';
import { getFareDirection } from './app';
import { findBySelected, getMainLegs } from './trips';
// Constants
import { DIRECTION } from '../const/app';
// Actions, Models & Interfaces
import { BasketItemModel, HotelFareModel, HotelModel, TariffModel, TripModel } from '../models';

export enum FLEXLANE_COVERAGE {
  FLIGHT_AND_HOTEL = 'flightAndHotel',
  FLIGHT_ONLY = 'flightOnly',
  HOTEL_ONLY = 'hotelOnly',
  ALL_ITEMS = 'allItems',
  REMOVED = 'removed',
}

const isTransportItem = (basketItem:BasketItemModel) => basketItem.assemblyTariffId > 0;

const isHotelItem = (basketItem:BasketItemModel) => basketItem.hotelFareId > 0;

export const getTripBasketItem = (
  basketItems:BasketItemModel[],
  trip:TripModel,
):BasketItemModel => {
  const tariffIds = getMainLegs(trip.legs)
    .map((leg) => findBySelected(leg.legOption.tariffs))
    .filter(isNotNil)
    .map((tariff:TariffModel) => tariff.id);

  return basketItems.find((basketItem) => tariffIds.includes(basketItem.assemblyTariffId));
};

export const getHotelBasketItem = (
  basketItems:BasketItemModel[],
  hotel:HotelModel,
):BasketItemModel => {
  const fare:HotelFareModel = findBySelected(hotel.fares);

  return isNil(fare) ? undefined : find(propEq('hotelFareId', Number(fare.id)), basketItems);
};

export const canOptOutOfFlexLane = (basketItem?:BasketItemModel):boolean =>
  basketItem?.travelBookingItem?.flexlaneOptOutAvailable === true;

// Product judges the FlexLane price threshold per direction, so a round trip sold on one ticket
// can come back covered outbound and uncovered inbound. An empty result (a backend older than
// lanes-planes/backend#15345, or fares with no direction) means "covered as a whole".
const getFlexLaneCoveredDirections = (basketItem?:BasketItemModel):DIRECTION[] => {
  const travelBookingItem = basketItem?.travelBookingItem;

  return (travelBookingItem?.flexlaneCoveredFareIds ?? [])
    .map((fareId:number) => getFareDirection(fareId, travelBookingItem))
    .filter(isNotNil);
};

export const isFlexLaneDirectionCovered = (
  basketItem:BasketItemModel,
  direction:DIRECTION,
):boolean => {
  const covered = getFlexLaneCoveredDirections(basketItem);

  return isNilOrEmpty(covered) || covered.includes(direction);
};

// One Travel::BookingItem sits behind both basket items of a one-ticket round trip, so its single
// choice would otherwise be offered, counted and toggled twice.
export const uniqueByTravelBookingItem:(basketItems:BasketItemModel[]) => BasketItemModel[] =
  uniqBy(path(['travelBookingItem', 'id']));

export const getFlexLaneCoverage = (basketItems:BasketItemModel[]):FLEXLANE_COVERAGE => {
  const offered = uniqueByTravelBookingItem((basketItems ?? []).filter(canOptOutOfFlexLane));

  if (isNilOrEmpty(offered)) {
    return null;
  }

  const covered = offered.filter(
    (basketItem) => !basketItem.travelBookingItem?.flexlaneOptedOut,
  );

  if (isNilOrEmpty(covered)) {
    return FLEXLANE_COVERAGE.REMOVED;
  }

  // The other values name the positions they cover; past two there is no room to name them all.
  if (covered.length === offered.length && offered.length > 2) {
    return FLEXLANE_COVERAGE.ALL_ITEMS;
  }

  const hasTransport = covered.some(isTransportItem);
  const hasHotel = covered.some(isHotelItem);

  if (hasTransport && hasHotel) {
    return FLEXLANE_COVERAGE.FLIGHT_AND_HOTEL;
  }

  return hasTransport ? FLEXLANE_COVERAGE.FLIGHT_ONLY : FLEXLANE_COVERAGE.HOTEL_ONLY;
};
